// ============================================================
// StepBasicDetails Component
// ============================================================

import { useState } from 'react';
import { Box, TextField, MenuItem, Grid, Avatar, Typography, Autocomplete, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import { Upload } from 'lucide-react';
import { studentsApi } from '@/api/students.api';
import { useSchool } from '@/hooks/useSchool';
import { BLOOD_GROUPS, GENDERS } from '@/constants/student';
import { getReligions, getCategoriesForReligion, getCastesForReligionAndCategory } from '@/constants/demographicsData';
import { 
  lettersOnlyRegex, 
  lettersAndSpacesRegex,
  nameRegex,
  digitsOnlyRegex, 
  decimalRegex, 
  createRestrictedHandler
} from '@/utils/inputRestrictions';

export function StepBasicDetails({ formik, isEditMode = false, initialData = null }) {
  const { values, errors, touched, handleChange, handleBlur } = formik;
  const { schoolId } = useSchool();
  const [validatingAadhaar, setValidatingAadhaar] = useState(false);
  const [aadhaarError, setAadhaarError] = useState('');

  const handleAadhaarBlur = async (e) => {
    handleBlur(e);
    const val = (e.target.value || '').trim();
    if (!val || val.length !== 12 || !schoolId) {
      setAadhaarError('');
      return;
    }
    const isExistingAadhaar = isEditMode && String(val).trim() === String(initialData?.student?.aadhaar_no || '').trim();
    if (isExistingAadhaar) {
      setAadhaarError('');
      return;
    }
    setValidatingAadhaar(true);
    setAadhaarError('');
    try {
      const res = await studentsApi.validateAadhaar(schoolId, val);
      const data = res?.data || res;
      if (data?.available === false) {
        const msg = data?.message || 'This Aadhaar is already associated with another student. Please enter the correct Aadhaar number for the student.';
        setAadhaarError(msg);
        formik.setFieldError('aadhaar_no', msg);
      } else {
        setAadhaarError('');
      }
    } catch (err) {
      const msg = err?.response?.data?.data?.message || err?.response?.data?.message || err?.message;
      if (msg) {
        setAadhaarError(msg);
        formik.setFieldError('aadhaar_no', msg);
      }
    } finally {
      setValidatingAadhaar(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          label="First Name *"
          name="first_name"
          value={values.first_name}
          onChange={createRestrictedHandler(handleChange, lettersAndSpacesRegex)}
          onBlur={handleBlur}
          error={touched.first_name && Boolean(errors.first_name)}
          helperText={touched.first_name && errors.first_name}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          label="Middle Name"
          name="middle_name"
          value={values.middle_name}
          onChange={createRestrictedHandler(handleChange, lettersAndSpacesRegex)}
          onBlur={handleBlur}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          label="Last Name *"
          name="last_name"
          value={values.last_name}
          onChange={createRestrictedHandler(handleChange, lettersAndSpacesRegex)}
          onBlur={handleBlur}
          error={touched.last_name && Boolean(errors.last_name)}
          helperText={touched.last_name && errors.last_name}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <Autocomplete
          options={GENDERS}
          value={values.gender || null}
          onChange={(event, newValue) => {
            formik.setFieldValue('gender', newValue || '');
          }}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Gender *"
              name="gender"
              onBlur={handleBlur}
              error={touched.gender && Boolean(errors.gender)}
              helperText={touched.gender && errors.gender}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          type="date"
          label="Date of Birth *"
          name="date_of_birth"
          slotProps={{ inputLabel: { shrink: true } }}
          value={values.date_of_birth}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.date_of_birth && Boolean(errors.date_of_birth)}
          helperText={touched.date_of_birth && errors.date_of_birth}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <Autocomplete
          forcePopupIcon={true}
          options={BLOOD_GROUPS}
          value={values.blood_group || null}
          onChange={(event, newValue) => {
            formik.setFieldValue('blood_group', newValue || '');
          }}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Blood Group"
              name="blood_group"
              onBlur={handleBlur}
              error={touched.blood_group && Boolean(errors.blood_group)}
              helperText={touched.blood_group && errors.blood_group}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          label="Aadhaar No"
          name="aadhaar_no"
          value={values.aadhaar_no}
          onChange={(e) => {
            createRestrictedHandler(handleChange, digitsOnlyRegex, 12)(e);
            if (aadhaarError) setAadhaarError('');
          }}
          onBlur={handleAadhaarBlur}
          error={Boolean(aadhaarError || (touched.aadhaar_no && errors.aadhaar_no))}
          helperText={
            validatingAadhaar
              ? 'Checking Aadhaar availability...'
              : aadhaarError || (touched.aadhaar_no && errors.aadhaar_no)
          }
          slotProps={{
            input: {
              endAdornment: validatingAadhaar ? (
                <CircularProgress color="inherit" size={18} />
              ) : null,
            },
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          label="Nationality"
          name="nationality"
          value={values.nationality || 'INDIAN'}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={true}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <Autocomplete
          freeSolo
          forcePopupIcon={true}
          options={getReligions()}
          value={values.religion || ''}
          onInputChange={(event, newInputValue) => {
            formik.setFieldValue('religion', newInputValue || '');
          }}
          onChange={(event, newValue) => {
            formik.setFieldValue('religion', newValue || '');
          }}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Religion"
              name="religion"
              onBlur={handleBlur}
              error={touched.religion && Boolean(errors.religion)}
              helperText={touched.religion && errors.religion}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <Autocomplete
          freeSolo
          forcePopupIcon={true}
          options={getCategoriesForReligion(values.religion)}
          value={values.category || ''}
          onInputChange={(event, newInputValue) => {
            formik.setFieldValue('category', newInputValue || '');
          }}
          onChange={(event, newValue) => {
            formik.setFieldValue('category', newValue || '');
          }}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Category"
              name="category"
              onBlur={handleBlur}
              error={touched.category && Boolean(errors.category)}
              helperText={touched.category && errors.category}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <Autocomplete
          freeSolo
          forcePopupIcon={true}
          options={getCastesForReligionAndCategory(values.religion, values.category)}
          value={values.caste || ''}
          onInputChange={(event, newInputValue) => {
            formik.setFieldValue('caste', newInputValue || '');
          }}
          onChange={(event, newValue) => {
            formik.setFieldValue('caste', newValue || '');
          }}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Caste"
              name="caste"
              onBlur={handleBlur}
              error={touched.caste && Boolean(errors.caste)}
              helperText={touched.caste && errors.caste}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          label="Height (cm)"
          name="height"
          value={values.height || ''}
          onChange={createRestrictedHandler(handleChange, decimalRegex)}
          onBlur={handleBlur}
          error={touched.height && Boolean(errors.height)}
          helperText={touched.height && errors.height}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          label="Weight (kg)"
          name="weight"
          value={values.weight || ''}
          onChange={createRestrictedHandler(handleChange, decimalRegex)}
          onBlur={handleBlur}
          error={touched.weight && Boolean(errors.weight)}
          helperText={touched.weight && errors.weight}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              name="is_rte"
              checked={values.is_rte !== undefined ? Boolean(values.is_rte) : true}
              onChange={handleChange}
              color="primary"
            />
          }
          label="RTE Student (Right to Education)"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Box
          component="label"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            bgcolor: 'primary.50',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'primary.100'
            }
          }}
        >
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                formik.setFieldValue('photoFile', file);
                const previewUrl = URL.createObjectURL(file);
                formik.setFieldValue('photoPreview', previewUrl);

                const reader = new FileReader();
                reader.onloadend = () => {
                  formik.setFieldValue('photo', reader.result);
                };
                reader.readAsDataURL(file);
              }
            }}
          />
          {values.photoPreview || values.photo ? (
            <Avatar
              src={values.photoPreview || values.photo}
              sx={{ width: 72, height: 72, mb: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            />
          ) : (
            <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: 'primary.100', mb: 1 }}>
              <Upload size={24} color="#1976d2" />
            </Box>
          )}
          <Typography variant="body2" fontWeight={600} color="primary.main">
            {values.photoPreview ? 'Change Photo' : 'Upload Photo'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Max 3MB (JPG, PNG)
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
